
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

/**
 * @typedef {Object} BlogPost
 * @property {string} id
 * @property {string} title
 * @property {string} slug
 * @property {string} excerpt
 * @property {string} content
 * @property {string} image_url
 * @property {string} published_at
 */

/** @type {BlogPost | null} */
const initialPost = null;

export default function BlogArticle() {
  const { slug } = useParams();
  /** @type {[BlogPost|null, Function]} */
  const [post, setPost] = useState(initialPost);
  const [loading, setLoading] = useState(true);
  /** @type {[BlogPost[], Function]} */
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  const loadPost = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) {
        console.error('Error fetching post:', error);
        setPost(null);
      } else if (data) {
        setPost(data);
        loadRelatedPosts(data.id);
      }
    } catch (e) {
      console.error('Unexpected error while loading post:', e);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedPosts = async (currentId) => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .neq('id', currentId)
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) {
        console.error('Error fetching related posts:', error);
        setRelatedPosts([]);
      } else if (data) {
        setRelatedPosts(data);
      }
    } catch (e) {
      console.error('Unexpected error while loading related posts:', e);
      setRelatedPosts([]);
    }
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navigation />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <i className="ri-loader-4-line text-3xl sm:text-4xl text-red-600 animate-spin"></i>
            <p className="mt-4 text-gray-400 text-sm sm:text-base">A carregar artigo...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black">
        <Navigation />
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center">
            <i className="ri-file-list-line text-4xl sm:text-6xl text-gray-600 mb-4"></i>
            <h1 className="text-xl sm:text-2xl font-bold text-red-100 mb-4">Artigo não encontrado</h1>
            <Link 
              to="/blog"
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              ← Voltar ao blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Article Header */}
      <article className="pt-24 sm:pt-32 pb-10 sm:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          {/* Breadcrumb */}
          <nav className="mb-6 sm:mb-8 overflow-x-auto">
            <div className="flex items-center text-xs sm:text-sm text-gray-400 whitespace-nowrap">
              <Link to="/" className="hover:text-red-400 transition-colors">Início</Link>
              <i className="ri-arrow-right-s-line mx-1 sm:mx-2"></i>
              <Link to="/blog" className="hover:text-red-400 transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line mx-1 sm:mx-2"></i>
              <span className="text-red-400 truncate max-w-[150px] sm:max-w-xs">{post.title}</span>
            </div>
          </nav>

          {/* Article Title */}
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-red-100 mb-4 sm:mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {post.title}
          </h1>

          {/* Article Info */}
          <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('pt-PT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              }) : ''}</span>
            </div>
          </div>

          {/* Featured Image */}
          {post.image_url && (
            <div className="mb-8 sm:mb-12 rounded-xl sm:rounded-2xl overflow-hidden">
              <img 
                src={post.image_url}
                alt={post.title}
                className="w-full h-56 sm:h-80 md:h-96 object-cover"
              />
            </div>
          )}

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed italic border-l-4 border-red-500 pl-3 sm:pl-4">
              {post.excerpt}
            </p>
          )}

          {/* Article Content */}
          <div className="prose prose-sm sm:prose-lg prose-invert max-w-none">
            <div 
              className="text-gray-300 leading-relaxed text-base sm:text-lg prose-headings:text-red-100 prose-h1:text-2xl sm:prose-h1:text-3xl prose-h2:text-xl sm:prose-h2:text-2xl prose-h3:text-lg sm:prose-h3:text-xl prose-p:mb-4 sm:prose-p:mb-6 prose-a:text-red-400 prose-a:hover:text-red-300 prose-blockquote:border-l-red-500 prose-blockquote:text-gray-400 prose-blockquote:italic prose-img:rounded-xl prose-img:my-6 sm:prose-img:my-8"
              style={{ fontFamily: "'Crimson Text', serif" }}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Share Section */}
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-800">
            <h3 className="text-lg sm:text-xl font-bold text-red-100 mb-4">Partilhar este artigo:</h3>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <button 
                onClick={() => navigator.share ? navigator.share({title: post.title, url: window.location.href}) : window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-facebook-line"></i>
                <span className="hidden sm:inline">Facebook</span>
              </button>
              <button 
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=${post.title}`)}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-twitter-x-line"></i>
                <span className="hidden sm:inline">Twitter</span>
              </button>
              <button 
                onClick={() => navigator.clipboard.writeText(window.location.href)}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-link"></i>
                <span className="hidden sm:inline">Copiar Link</span>
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-10 sm:py-16 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-red-100 mb-6 sm:mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
              Outros Artigos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`} onClick={handleLinkClick}>
                  <article className="group cursor-pointer transform hover:scale-105 transition-all duration-500">
                    <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl border border-red-900/30 hover:border-red-500/50 transition-all duration-300">
                      <div className="relative h-40 sm:h-48 overflow-hidden">
                        {relatedPost.image_url ? (
                          <img 
                            src={relatedPost.image_url}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                            <i className="ri-image-line text-2xl sm:text-3xl text-gray-500"></i>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      </div>
                      <div className="p-4 sm:p-6">
                        <h3 className="text-base sm:text-lg font-bold text-red-100 mb-2 sm:mb-3 group-hover:text-red-300 transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {relatedPost.title}
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                        <div className="flex items-center text-xs text-gray-500">
                          <i className="ri-calendar-line mr-1"></i>
                          <span>{relatedPost.published_at ? new Date(relatedPost.published_at).toLocaleDateString('pt-PT') : ''}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <section className="py-8 sm:py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Link 
            to="/blog"
            onClick={handleLinkClick}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:from-red-700 hover:to-red-900 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-red-500/25 border border-red-700/50 text-sm sm:text-base"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao Blog
          </Link>
        </div>
      </section>

      <Footer />

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
